import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Inbox, LifeBuoy, Link2, MessageSquareWarning, RefreshCw, Search, Timer, UserCheck, X } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { AgeIndicator, hoursSince, InitialsAvatar, SegmentedControl, StatTile } from '../components/ui/queueUi'
import {
  ACTIVE_STATUSES,
  bucketForStatus,
  memberSinceLabel,
  PlanBadge,
  tagChipClass,
  TicketStatusBadge,
  ticketRailClass,
  ticketRef,
  workflowBucketOptions,
  type WorkflowBucket
} from '../components/support/supportUi'
import { useAuth } from '../hooks/useAuth'
import {
  assignTicket,
  getSupportAgents,
  getSupportTickets,
  type DateFilter,
  type SubscriptionFilter,
  type SupportAgent,
  type SupportTicketStatus,
  type SupportTicketSummary
} from '../lib/api/support'

const PAGE_SIZE = 100

type AssignmentFilter = 'all' | 'unassigned' | 'mine'

const assignmentFilters: Array<{ value: AssignmentFilter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'mine', label: 'Mine' }
]

const dateFilters: Array<{ value: DateFilter; label: string }> = [
  { value: 'all', label: 'All time' },
  { value: 'month', label: 'Last month' },
  { value: 'week', label: 'Last week' },
  { value: '3days', label: 'Last 3 days' },
  { value: 'today', label: 'Today' }
]

const subscriptionFilters: Array<{ value: SubscriptionFilter; label: string }> = [
  { value: 'all', label: 'All plans' },
  { value: 'paid', label: 'Paid' },
  { value: 'trial', label: 'Trial' },
  { value: 'free', label: 'Free' }
]

const isNeedsReply = (status: SupportTicketStatus) => bucketForStatus(status) === 'needs_reply'

export const SupportQueuePage = () => {
  const { user } = useAuth()
  const [activeTickets, setActiveTickets] = useState<SupportTicketSummary[]>([])
  const [resolvedTickets, setResolvedTickets] = useState<SupportTicketSummary[]>([])
  const [activeHasMore, setActiveHasMore] = useState(false)
  const [resolvedHasMore, setResolvedHasMore] = useState(false)
  const [resolvedLoaded, setResolvedLoaded] = useState(false)
  const [bucket, setBucket] = useState<WorkflowBucket>('needs_reply')
  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>('all')
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')
  const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [oldestFirst, setOldestFirst] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [agents, setAgents] = useState<SupportAgent[]>([])
  const [claimingId, setClaimingId] = useState<string | null>(null)

  const needsResolved = bucket === 'resolved' || bucket === 'all'

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    getSupportAgents()
      .then(setAgents)
      .catch((err) => console.error('Failed to load support agents', err))
  }, [])

  const fetchPage = useCallback(
    (statuses: SupportTicketStatus[], cursor?: string) =>
      getSupportTickets({
        statuses,
        since: dateFilter !== 'all' ? dateFilter : undefined,
        search: debouncedSearch || undefined,
        subscription: subscriptionFilter !== 'all' ? subscriptionFilter : undefined,
        limit: PAGE_SIZE,
        cursor
      }),
    [dateFilter, debouncedSearch, subscriptionFilter]
  )

  const loadTickets = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const active = await fetchPage(ACTIVE_STATUSES)
      setActiveTickets(active)
      setActiveHasMore(active.length === PAGE_SIZE)
      setResolvedLoaded(false)
      setResolvedTickets([])
      setResolvedHasMore(false)
    } catch (err) {
      console.error('Failed to load tickets', err)
      setError('Unable to load support tickets.')
    } finally {
      setLoading(false)
    }
  }, [fetchPage])

  useEffect(() => {
    void loadTickets()
  }, [loadTickets])

  useEffect(() => {
    if (!needsResolved || resolvedLoaded) return
    let cancelled = false
    fetchPage(['Resolved'])
      .then((resolved) => {
        if (cancelled) return
        setResolvedTickets(resolved)
        setResolvedHasMore(resolved.length === PAGE_SIZE)
        setResolvedLoaded(true)
      })
      .catch((err) => {
        console.error('Failed to load resolved tickets', err)
        if (!cancelled) setError('Unable to load resolved tickets.')
      })
    return () => {
      cancelled = true
    }
  }, [needsResolved, resolvedLoaded, fetchPage])

  const loadMore = async () => {
    if (loadingMore) return
    setLoadingMore(true)
    try {
      const wantsActive = bucket !== 'resolved' && activeHasMore
      const wantsResolved = needsResolved && resolvedHasMore
      if (wantsActive) {
        const cursor = activeTickets[activeTickets.length - 1]?.lastMessageAt
        const page = await fetchPage(ACTIVE_STATUSES, cursor)
        setActiveTickets((current) => [...current, ...page.filter((item) => !current.some((existing) => existing.id === item.id))])
        setActiveHasMore(page.length === PAGE_SIZE)
      }
      if (wantsResolved) {
        const cursor = resolvedTickets[resolvedTickets.length - 1]?.lastMessageAt
        const page = await fetchPage(['Resolved'], cursor)
        setResolvedTickets((current) => [...current, ...page.filter((item) => !current.some((existing) => existing.id === item.id))])
        setResolvedHasMore(page.length === PAGE_SIZE)
      }
    } catch (err) {
      console.error('Failed to load more tickets', err)
      setError('Unable to load more tickets.')
    } finally {
      setLoadingMore(false)
    }
  }

  const handleClaim = async (ticket: SupportTicketSummary) => {
    if (!user || claimingId) return
    setClaimingId(ticket.id)
    try {
      const updated = await assignTicket(ticket.id, 'me', ticket.assignmentRevision)
      setActiveTickets((current) =>
        current.map((item) =>
          item.id === updated.id
            ? { ...item, assignedAgentId: updated.assignedAgentId, assignmentRevision: updated.assignmentRevision, status: updated.status }
            : item
        )
      )
    } catch (err) {
      console.error('Failed to claim ticket', err)
      setError('Unable to claim that ticket. It may have just been assigned — refresh to see the latest.')
    } finally {
      setClaimingId(null)
    }
  }

  const visibleTickets = useMemo(() => {
    let tickets: SupportTicketSummary[]
    if (bucket === 'resolved') {
      tickets = resolvedTickets
    } else if (bucket === 'all') {
      tickets = [...activeTickets, ...resolvedTickets]
    } else {
      tickets = activeTickets.filter((ticket) => bucketForStatus(ticket.status) === bucket)
    }

    if (assignmentFilter === 'unassigned') {
      tickets = tickets.filter((ticket) => !ticket.assignedAgentId)
    } else if (assignmentFilter === 'mine') {
      tickets = tickets.filter((ticket) => ticket.assignedAgentId === user?.id)
    }

    return [...tickets].sort((a, b) => {
      const byAge = new Date(a.lastMessageAt).getTime() - new Date(b.lastMessageAt).getTime()
      return oldestFirst ? byAge : -byAge
    })
  }, [activeTickets, resolvedTickets, bucket, assignmentFilter, oldestFirst, user?.id])

  const stats = useMemo(() => {
    const needsReply = activeTickets.filter((ticket) => isNeedsReply(ticket.status))
    const oldestWaitingHours = needsReply.reduce((max, ticket) => Math.max(max, hoursSince(ticket.lastMessageAt)), 0)
    return {
      open: activeTickets.length,
      needsReply: needsReply.length,
      unassigned: activeTickets.filter((ticket) => !ticket.assignedAgentId).length,
      waiting: activeTickets.filter((ticket) => bucketForStatus(ticket.status) === 'waiting').length,
      oldestWaitingHours
    }
  }, [activeTickets])

  const hasMore = (bucket !== 'resolved' && activeHasMore) || (needsResolved && resolvedHasMore)

  return (
    <div className="space-y-4 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold">
            <LifeBuoy className="h-5 w-5 text-primary" />
            Support Inbox
          </h1>
          <p className="text-sm text-muted-foreground">
            Answer customers, hand off bugs, and keep response times short. Red means someone is waiting on us.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadTickets()} disabled={loading}>
          <RefreshCw className={`mr-1 h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <StatTile
          label="Open tickets"
          value={stats.open}
          icon={<Inbox className="h-5 w-5" />}
          onClick={() => { setBucket('all'); setAssignmentFilter('all') }}
        />
        <StatTile
          label="Need a reply"
          value={stats.needsReply}
          icon={<MessageSquareWarning className="h-5 w-5" />}
          tone="danger"
          active={bucket === 'needs_reply'}
          onClick={() => { setBucket('needs_reply'); setAssignmentFilter('all') }}
        />
        <StatTile
          label="Unassigned"
          value={stats.unassigned}
          icon={<UserCheck className="h-5 w-5" />}
          tone="amber"
          active={assignmentFilter === 'unassigned'}
          onClick={() => setAssignmentFilter(assignmentFilter === 'unassigned' ? 'all' : 'unassigned')}
        />
        <StatTile
          label="Waiting on others"
          value={stats.waiting}
          icon={<Timer className="h-5 w-5" />}
          active={bucket === 'waiting'}
          onClick={() => { setBucket('waiting'); setAssignmentFilter('all') }}
        />
        <StatTile
          label="Oldest waiting"
          value={stats.needsReply === 0 ? '—' : `${Math.round(stats.oldestWaitingHours)}h`}
          icon={<ArrowDownWideNarrow className="h-5 w-5" />}
          tone={stats.oldestWaitingHours >= 24 ? 'danger' : 'default'}
          onClick={() => { setBucket('needs_reply'); setOldestFirst(true) }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl options={workflowBucketOptions} value={bucket} onChange={setBucket} />
        <SegmentedControl options={assignmentFilters} value={assignmentFilter} onChange={setAssignmentFilter} />
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search name, ticket, keyword…"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="h-8 pl-9 text-sm"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select
          aria-label="Filter by plan"
          value={subscriptionFilter}
          onChange={(event) => setSubscriptionFilter(event.target.value as SubscriptionFilter)}
          className="h-8 rounded-lg border border-border bg-background px-2 text-xs font-medium text-muted-foreground"
        >
          {subscriptionFilters.map((filter) => (
            <option key={filter.value} value={filter.value}>
              {filter.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by date"
          value={dateFilter}
          onChange={(event) => setDateFilter(event.target.value as DateFilter)}
          className="h-8 rounded-lg border border-border bg-background px-2 text-xs font-medium text-muted-foreground"
        >
          {dateFilters.map((filter) => (
            <option key={filter.value} value={filter.value}>
              {filter.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setOldestFirst((current) => !current)}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
        >
          {oldestFirst ? <ArrowUpNarrowWide className="h-3.5 w-3.5" /> : <ArrowDownWideNarrow className="h-3.5 w-3.5" />}
          {oldestFirst ? 'Oldest first' : 'Newest first'}
        </button>
      </div>

      {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading && <p className="p-6 text-center text-sm text-muted-foreground">Loading support tickets…</p>}
      {!loading && visibleTickets.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <Inbox className="mx-auto mb-3 h-8 w-8 opacity-40" />
          <p className="text-sm">No tickets match these filters. Adjust the filters or search to widen the queue.</p>
        </div>
      )}

      <div className="space-y-2">
        {visibleTickets.map((ticket) => {
          const assignedAgentName = ticket.assignedAgentId
            ? agents.find((agent) => agent.id === ticket.assignedAgentId)?.displayName ?? 'Assigned'
            : null
          const resolved = bucketForStatus(ticket.status) === 'resolved'
          return (
            <Link
              key={ticket.id}
              to={`/support/${encodeURIComponent(ticket.id)}`}
              className={`group flex w-full items-start gap-4 rounded-xl border border-l-4 border-border/60 bg-card p-4 text-left transition hover:border-primary/40 hover:shadow-elevation-1 ${ticketRailClass(ticket.status)}`}
            >
              <div className="flex w-14 shrink-0 flex-col items-start gap-1 pt-0.5">
                <AgeIndicator since={ticket.lastMessageAt} muted={resolved || !isNeedsReply(ticket.status)} />
                <span className="font-mono text-[11px] text-muted-foreground">{ticketRef(ticket.id)}</span>
              </div>

              <div className="min-w-0 flex-1">
                <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                  <TicketStatusBadge status={ticket.status} />
                  <span className="text-sm font-semibold text-foreground">{ticket.category || 'General'}</span>
                </div>
                <p className="truncate text-sm text-muted-foreground" title={ticket.lastMessagePreview}>
                  {ticket.lastMessagePreview}
                </p>
                {(ticket.tags.length > 0 || ticket.linkedUserIds.length > 0) && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {ticket.tags.slice(0, 4).map((tag) => (
                      <span key={tag} className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${tagChipClass(tag)}`}>
                        {tag}
                      </span>
                    ))}
                    {ticket.tags.length > 4 && (
                      <span className="text-[11px] text-muted-foreground">+{ticket.tags.length - 4} more</span>
                    )}
                    {ticket.linkedUserIds.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title="Has linked accounts">
                        <Link2 className="h-3 w-3" />
                        +{ticket.linkedUserIds.length} linked
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="hidden w-44 shrink-0 items-center gap-2.5 self-center md:flex">
                <InitialsAvatar name={ticket.username} imageUrl={ticket.profileImageUrl} size="h-7 w-7" />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-semibold text-foreground">
                    {ticket.username || ticket.userId.slice(0, 12)}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5">
                    <PlanBadge tier={ticket.subscriptionTier} status={ticket.subscriptionStatus} />
                    {memberSinceLabel(ticket.userCreatedAt) && (
                      <span className="font-mono text-[10px] text-muted-foreground">{memberSinceLabel(ticket.userCreatedAt)}</span>
                    )}
                  </span>
                </span>
              </div>

              <div className="flex w-28 shrink-0 items-center justify-end gap-2 self-center">
                {ticket.assignedAgentId ? (
                  <span
                    className="flex items-center gap-2 text-xs text-muted-foreground"
                    title={`Assigned to ${assignedAgentName ?? 'an agent'}`}
                  >
                    <InitialsAvatar name={assignedAgentName} size="h-6 w-6" />
                    <span className="hidden max-w-20 truncate lg:inline">
                      {ticket.assignedAgentId === user?.id ? 'You' : assignedAgentName}
                    </span>
                  </span>
                ) : !resolved ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={claimingId === ticket.id}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      void handleClaim(ticket)
                    }}
                  >
                    {claimingId === ticket.id ? 'Claiming…' : 'Claim'}
                  </Button>
                ) : null}
              </div>
            </Link>
          )
        })}
      </div>

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more tickets'}
          </Button>
        </div>
      )}
    </div>
  )
}
